import React, { useEffect, useState, useMemo } from 'react';
import {
  Container, Typography, Chip, Box, Alert, Button, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField
} from '@mui/material';
import AdminTable from '../../components/shared/AdminTable';
import billingApi from '../../services/billingApi';
import { formatDate } from '../../utils/formatters';

function statusColor(status) {
  const colors = { succeeded: 'success', failed: 'error', refunded: 'default', partially_refunded: 'warning', disputed: 'error' };
  return colors[status] || 'default';
}

export default function PaymentManagement() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refundDialog, setRefundDialog] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');

  useEffect(() => { loadPayments(); }, []);

  async function loadPayments() {
    setLoading(true);
    try {
      const data = await billingApi.get('/api/admin/payments?limit=100');
      setPayments(data.payments);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleRefund() {
    try {
      const body = { reason: refundReason };
      if (refundAmount) body.amount_cents = parseInt(refundAmount);
      await billingApi.post(`/api/admin/payments/${refundDialog}/refund`, body);
      setRefundDialog(null);
      setRefundAmount('');
      setRefundReason('');
      loadPayments();
    } catch (err) {
      setError(err.message);
    }
  }

  const columns = useMemo(() => [
    {
      field: 'created_at',
      headerName: 'Date',
      width: 170,
      type: 'date',
      renderCell: (row) => formatDate(row.created_at),
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 220,
      type: 'text',
    },
    {
      field: 'productId',
      headerName: 'Product',
      width: 140,
      type: 'text',
      renderCell: (row) => row.productId || '\u2014',
    },
    {
      field: 'net_amount_cents',
      headerName: 'Amount',
      width: 120,
      type: 'numeric',
      align: 'right',
      renderCell: (row) => `$${(row.net_amount_cents / 100).toFixed(2)}`,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      type: 'singleSelect',
      filterOptions: ['succeeded', 'failed', 'refunded', 'partially_refunded', 'disputed'],
      renderCell: (row) => (
        <Chip label={row.status} color={statusColor(row.status)} size="small" />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (row) => row.status === 'succeeded' ? (
        <Button size="small" onClick={(e) => { e.stopPropagation(); setRefundDialog(row.id); }}>Refund</Button>
      ) : null,
    },
  ], []);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>Payments</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined" sx={{ height: 600 }}>
        <AdminTable
          rows={payments}
          columns={columns}
          loading={loading && payments.length === 0}
          defaultSort={[{ field: 'created_at', direction: 'desc' }]}
          pageKey="payment_management.grid"
        />
      </Paper>

      <Dialog open={!!refundDialog} onClose={() => setRefundDialog(null)}>
        <DialogTitle>Issue Refund</DialogTitle>
        <DialogContent>
          <TextField fullWidth type="number" label="Amount (cents, leave empty for full)" value={refundAmount} onChange={e => setRefundAmount(e.target.value)} sx={{ mt: 1, mb: 2 }} />
          <TextField fullWidth label="Reason" value={refundReason} onChange={e => setRefundReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialog(null)}>Cancel</Button>
          <Button color="error" onClick={handleRefund}>Refund</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
